import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import taxService from '../../services/taxService';

interface ProductTaxCodeDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (code: string) => void;
}

interface TaxCode {
  id: string;
  name: string;
}

const ProductTaxCodeDialog: React.FC<ProductTaxCodeDialogProps> = ({ open, onClose, onSelect }) => {
  const [codes, setCodes] = useState<TaxCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchCodes = async () => {
      try {
        setLoading(true);
        const list = await taxService.getTaxCodes({ limit: 1000 });
        setCodes(Array.isArray(list) ? list : []);
      } catch (err: any) {
        console.error('[ProductTaxCodeDialog] Failed to fetch tax codes', err);
        setError(err?.response?.data?.detail || err.message || 'Failed to fetch tax codes');
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a Product Tax Code</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <List dense>
            {codes.map((code) => (
              <ListItemButton key={code.id} onClick={() => onSelect(code.id)}>
                <ListItemText primary={`${code.id} – ${code.name}`} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Skip</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductTaxCodeDialog;
